import { useEffect, useRef } from 'react';

import { autoUpdate } from '@floating-ui/dom';
import { RoughSVG } from 'roughjs/bin/svg';

import { useThemeColor } from '@cast/design-system';

import {
  createStableRenderer,
  drawArrow,
  drawArrowHead,
  getArrowHeadPoints,
  getLinePoints,
  getStartEndPositions,
} from './arrowUtils';

type Props = {
  fromElement: HTMLElement;
  toElement: HTMLElement;
};

export function TourStepArrow(props: Props) {
  const color = useThemeColor('indigo.600');
  const svgRef = useRef<SVGSVGElement>(null);
  const rendererRef = useRef<RoughSVG | null>(null);

  useEffect(() => {
    function renderArrow() {
      const svg = svgRef.current;

      if (!svg) {
        return;
      }

      if (!rendererRef.current) {
        rendererRef.current = createStableRenderer(svg);
      }

      const [fromRect, toRect] = getStartEndPositions(
        props.fromElement,
        props.toElement
      );

      // clean up previously rendered arrow
      svg.replaceChildren();

      const linePoints = getLinePoints(fromRect, toRect);
      const line = drawArrow(rendererRef.current, linePoints, color);
      const arrowHead = drawArrowHead(
        rendererRef.current,
        getArrowHeadPoints(linePoints),
        color
      );

      svg.appendChild(line);
      svg.appendChild(arrowHead);

      // bbox allows us to use element's coordinates as they appear on the screen
      // instead of normalizing them into the svg's coordinates
      const bbox = svg.getBBox();
      svg.setAttribute(
        'viewBox',
        `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`
      );
      svg.setAttribute('width', `${bbox.width}px`);
      svg.setAttribute('height', `${bbox.height}px`);

      const top = fromRect.top < toRect.top ? 0 : 'unset';
      const bottom = top === 'unset' ? 0 : 'unset';
      const left = fromRect.left < toRect.left ? 0 : 'unset';
      const right = left === 'unset' ? 0 : 'unset';

      Object.assign(svg.style, {
        top,
        bottom,
        left,
        right,
      });
    }

    return autoUpdate(props.fromElement, props.toElement, renderArrow, {
      animationFrame: true, // required to adjust position when pointing to fixed elements such as tooltips
    });
  }, [color, props.fromElement, props.toElement]);

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      style={{ pointerEvents: 'none', position: 'absolute' }}
    />
  );
}
